import React, { createContext, useEffect, useState } from "react";

import { useRouter } from "next/router";
import { getAPIAxios } from "../helper/constants/uri";
import {
    initFBTracking,
    shAnalyticsGetCourseTrackitem,
    shAnalyticsParseAboMode,
    shAnalyticsSetUserId,
    shAnalyticsSetUserProperties, shSIBIdentify, shSIBTrackEvent, trackClearECommerce,
    trackEvent, trackFBEvent
} from "../helper/tracking/tracking";
import useAsyncReference from "../helper/hooks/asyncReference";
import { isCourseComingSoon } from "../helper/course/course-helper";
import defaultPaymentInfoServiceInstance from "./paymentInfoService";
import posthog from "posthog-js";
import {DateTime} from "luxon";
import authState, { CurrentUserOverviewType } from "../zustand/authState";
import usePaymentCheckoutState from "../zustand/payment/checkout/paymentCheckoutState";
import { usePaymentCheckoutCourseState } from "../zustand/payment/checkout/paymentCheckoutCourseState";
import useInfoState from "../zustand/infoState";
import useAuthState from "../zustand/authState";
import { PasswordRecoveryService } from "../generated";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from "@firebase/auth";
import { getAuth } from "firebase/auth";
import { initFirebaseApp } from "../helper/firebase/firebase";
import FirebaseWrapper from "./firebaseWrapper";

declare var window: any;
export const AuthContext = createContext(null);

initFirebaseApp();

export const AuthProvider = ( { children } ) => {

    const [ isLoggedIn, setIsLoggedIn ] = useState(null);
    const [ currentUserMail, setCurrentUserMail ] = useState(null);

    const [ pageMode, setPageMode ] = useState('normal');
    const [ agbCheck, setAgbCheck ]= useState(true);

    const [ courseOverview, setCourseOverview ] = useState('normal');
    const [ currentUserOverview, setCurrentUserOverviewState ] = useAsyncReference<CurrentUserOverviewType>(null);
    const setCurrentUserOverview = (overview) => {
        setCurrentUserOverviewState(overview);
        authState.getState().setCurrentUserOverview(overview);

        if (typeof window !== 'undefined') {
            window.currentUserOverview = overview;
        }
    }

    const [ currentModal, setCurrentInternalModal ]= useAsyncReference(null);

    const setCurrentModal = (m) => {
        // always clear all tracked steps before any use effect etc.
        // (to avoid any sideeffects etc.) - would be better solved with zustand
        if (m === "checkout") {
            usePaymentCheckoutState.getState().clearTrackedSteps();
        }

        setCurrentInternalModal(m);
    };

    const [ currentModalErrorMessage, setCurrentModalErrorMessage ] = useState(null);
    const [ showMobileOverlay, setShowMobileOverlay ]= useState(false);

    const setShowCheckoutDialog = usePaymentCheckoutState(s => s.setShowCheckoutDialog);
    const setStep = usePaymentCheckoutState( s => s.setStep);

    const setBuyMode = usePaymentCheckoutState(s => s.setBuyMode);

    const currentCourse = usePaymentCheckoutCourseState(s => s.course);
    const setCurrentCourse = usePaymentCheckoutCourseState(s => s.setCourse);

    const setCurrentProduct = usePaymentCheckoutState(s => s.setCurrentProduct);

    const setIsGiftcard = usePaymentCheckoutState(s => s.setIsGiftcard);
    const isGiftcard = usePaymentCheckoutState(s => s.isGiftcard);
    const setSkipPassChoose = usePaymentCheckoutState(s => s.setSkipPassChoose);

    const handlePassBuy = async (course = null, aboType = null ) => {
        // but show pass buy modal
        setBuyMode("abo");

        if(aboType === "family"){
            setCurrentProduct(useInfoState.getState().aboFamily);
        } else if(aboType === "duo" || aboType === null){
            setCurrentProduct(useInfoState.getState().aboDuo);
        }

        const trackItem = shAnalyticsParseAboMode("yearly");

        const trackAction = () => {
            if (!trackItem) {
                console.log('*** error no track item');
                return;
            }

            trackClearECommerce();
        };

        if (isLoggedIn) {

            if (currentUserOverview?.current?.abo
                && !currentUserOverview?.current?.canRenewAbo
                && !(usePaymentCheckoutState.getState().isGiftcard)
            ) {

                if (!course) {
                    await router.replace("/profil/kurse");
                } else {
                    await router.replace("/kochkurs-enrolled/" + course.uuid);
                }

                window.scrollTo(0, 0);

                return;
            }


            // check if maybe special abo discount code
            const renewCode = currentUserOverview.current?.aboCustomDiscountCode;

            if (renewCode) {
                await defaultPaymentInfoServiceInstance.setVoucherCode(renewCode);
            }


            setShowCheckoutDialog(true);
            trackAction();

            window.addToCart = {
                trackItem,
                value: trackItem.price
            };

        } else {
            setShowCheckoutDialog(true);
            setStep("register");

            trackAction();
            trackEvent('passBuyRegisterBefore');
        }
    };

    const handlePassGiftcardBuy = (course = null) => {
        setIsGiftcard(true);
        setSkipPassChoose(true);
        handlePassBuy(course, "aboBasic");
    };

    const handleCourseBuy = (course) => {
        setBuyMode("course");
        setCurrentCourse(course);
        const trackItem = shAnalyticsGetCourseTrackitem(course, isGiftcard ? 'course-giftcard' : 'course');

        if (isLoggedIn) {
            window.addToCart = {
                trackItem,
                value: trackItem.price
            };

            setShowCheckoutDialog(true);
        } else {
            trackEvent('courseBuyRegisterBefore');
            setShowCheckoutDialog(true);
            setStep("register")
        }

    }

    const handleCourseGiftcardBuy = (course) => {
        setIsGiftcard(true);
        handleCourseBuy(course);
    }

    const handleLogin = (clean = false) => {
        if (clean) {
            setIsGiftcard(false);
            setCurrentCourse(null);
        }

        trackEvent('showLoginModal');
        setCurrentModal("login");
    }

    const hideModal = (hideModal = true) => {
        removeFromCartEvent();

        if (hideModal) {
            setCurrentModal(null);
        }

        // reset any other values (to ensure clean reset)
        setCurrentCourse(null);
        setIsGiftcard(false);
        setCurrentModalErrorMessage(null);
    };

    const showBuyModal = () => {
        setIsGiftcard(false);
        setShowCheckoutDialog(true);
    };

    const removeFromCartEvent = () => {
        if (typeof window !== 'undefined' && window.addToCart) {
            try {
                trackEvent('remove_from_cart', {
                    currency: 'EUR',
                    value: window.addToCart.value,
                    items: [window.addToCart.trackItem]
                });

                shSIBTrackEvent('remove_from_cart', {
                    item: window.addToCart.trackItem.item_id,
                    product: window.addToCart.trackItem.item_name,
                    value: window.addToCart.trackItem.item_price,
                    abort_url: window.location.pathname
                });

            } catch(err) {

            }

            window.addToCart = undefined;
        }
    };

    const router = useRouter();

    const navigateToCourse = async (course, preview = false, videoId, recipeId) => {
        const courseId = course.id;
        const courseUuid = course.uuid;
        const courseSlug = course.slug;

        // check if enrolled
        if(hasBoughtCourse(courseId) && (!isCourseComingSoon(course))) {

            if (videoId) {
                await router.push("/kochkurs-enrolled/" + courseUuid + "/?searchVideoId=" + videoId);
            } else if (recipeId) {
                await router.push("/kochkurs-enrolled/" + courseUuid + "/?searchRecipeId=" + recipeId);
            } else {
                await router.push("/kochkurs-enrolled/" + courseUuid);
            }

        } else {

            if (!courseSlug) {
                throw new Error("course navigation error");
                return;
            }

            trackClearECommerce();
            trackEvent(
                "select_item",
                {
                    ecommerce: {
                        items: [
                            {
                                item_id: courseId,
                                item_name: course.title,
                                price: course.price,
                                quantity: 1,
                                item_list_name: "Courses Landingpage",
                                item_list_id: (courseId > 9 ? "C0" : "C00") + courseId,
                                item_category: "Kurs",
                                index: 0,
                                currency: "EUR",
                                affiliation: "Website",
                                discount: course ? defaultPaymentInfoServiceInstance.calculatePriceForCourse(course).discountPrice : null
                            }
                        ]
                    }
                }
            );

            await router.push("/kochkurse/" + courseSlug  + (preview ? "?preview=true" : ""));
        }

        window.scrollTo(0,0);
    }

    useEffect(
        () => {
            const action = async () => {
                const auth = getAuth();

                auth.onAuthStateChanged(async (user) => {

                    if (user) {
                        const idToken = (await user.getIdToken());
                        useAuthState.getState().setIdToken(idToken);

                        setIsLoggedIn(true);
                        setCurrentUserMail(user.email);

                        if (currentModal.current === 'account') {
                            hideModal();
                        }

                        await updateOwnOverview();

                        if (window.registerAction) {
                            // update profile
                            const apiAxios = getAPIAxios();
                            await apiAxios.post("/own-profile", {
                                firstname: window.registerAction.firstname,
                                lastname: window.registerAction.lastname
                            });

                            window.registerAction = undefined;
                        }

                        // tracking Landingpage "Gratis Lektionen" events (Lead FB Event)
                        if (router.pathname.indexOf("lp/gratis-lektionen") !== -1) {
                            const createdAt = DateTime.fromRFC2822(user.metadata.creationTime);
                            const minutesDiff = DateTime.now().diff(createdAt).as('minutes');

                            if (Math.abs(minutesDiff) < 5) {
                                trackFBEvent("Lead");
                            }
                        }

                        // ### if logged in - then just simple hide modal
                        if (currentModal.current === "login" ||
                            currentModal.current === "register") {
                            hideModal();

                            const exludedRoutes = [
                                "einloesen", "blog", "profil/", "testimonial",
                                "lp/gratis-lektionen", "reset", "experiences"
                            ];

                            for (const e of exludedRoutes) {
                                if (router.pathname.indexOf(e) !== -1) {
                                    return;
                                }
                            }

                            await router.push("/profil/kurse");
                        }

                        // #### special logic - if coming from redeem process
                        if (window.redeem) {
                            const axios = getAPIAxios();
                            await axios.post("/giftcard/redeem", window.redeem);
                            await updateOwnOverview();
                            await router.push("/profil/kurse");
                        }

                    } else {
                        setIsLoggedIn(false);
                        setCurrentUserMail(null);
                        setCurrentUserOverview(null);

                        useAuthState.getState().setIdToken(null);

                        posthog.reset();

                        if (window.signOut403) {
                            handleLogin();
                            window.signOut403 = false;
                        }
                    }
                });

                auth.onIdTokenChanged(
                    async (user) => {
                        if (!user) {
                            // clean any id token (for safety)
                            useAuthState.getState().setIdToken(null);
                            return;
                        }

                        const idToken = await user.getIdToken();
                        useAuthState.getState().setIdToken(idToken);

                        setCurrentUserMail(user.email);

                        // aLways update own overview
                        await updateOwnOverview();
                    }
                )
            };
            action();
        },
        []
    );

    const updateOwnOverview = async () => {
        try {
            const axios = getAPIAxios();
            const res = await axios.get("/own-overview");
            const data = res.data;
            setCurrentUserOverview(data);


            if (data.internal) {
                posthog.opt_out_capturing();
            }

            // update auth state internally too
            useAuthState.getState().setCurrentUserOverview(data);

            shAnalyticsSetUserProperties(
                {
                    boughtCourses: data.boughtCoursesId ? data.boughtCoursesId.length : 0,
                    likedRecipes: data.likedRecipesId ? data.likedRecipesId.length : 0,
                    abo: data.abo,
                    country: data.country,
                    databaseId: data.id,
                    email: data.mail,
                    mail: data.mail,
                    firstname: data.firstname,
                    lastname: data.lastname,
                    user_id: data.uuid
                }
            );

            try {
                posthog.identify(
                    data.id, // distinct_id, required
                    {
                        email: data.mail,
                        isInternal: data.isInternal,
                        abo: data.abo,
                        country: data.country,
                        boughtCourses: data.boughtCoursesId ? data.boughtCoursesId.length : 0,
                        likedRecipes: data.likedRecipesId ? data.likedRecipesId.length : 0,
                        databaseId: data.id,
                        setShowAboFamilyTeasers: data.setShowAboFamilyTeasers
                    }
                );
            } catch(err) {

            }

            shAnalyticsSetUserId(data.uuid);

            shSIBIdentify(data.mail);

            initFBTracking(
                {
                    mail: data.mail,
                    firstname: data.firstname,
                    lastname: data.lastname,
                }
            );

        } catch(err) {
            // ignore
        }
    }

    const login = async(email, password) => {
        try {
            const auth = getAuth();
            const res = await signInWithEmailAndPassword(auth, email, password);

            trackEvent('login', { method: 'password'});
            return res;
        } catch(err) {
            return err;
        }
    }

    const register = async(email, password, firstname = null, lastname = null) => {
        if (firstname || lastname) {
            // so that firebase handler can fetch it and update
            window.registerAction = { firstname, lastname };
        }

        let code = "success";
        let message = "";
        let isMailError = false;
        let isPasswordError = false;

        try {
            const auth = getAuth();
            await createUserWithEmailAndPassword(auth, email, password);

            trackFBEvent('CompleteRegistration');
        } catch(err) {
            code = err?.code;

            if (code === 'auth/email-already-in-use') {
                message = "Deine E-Mail-Adresse ist bereits registriert.";
                isMailError = true;

                try {
                    await login(email, password);
                } catch(err) {

                }
            } else if (err?.code === 'auth/invalid-password') {
                message = "Das vergebene Passwort ist zu schwach.";
                isPasswordError = true;
            } else if (err?.code === 'auth/weak-password') {
                message = "Das Passwort muss mindestens 6 Zeichen haben.";
                isPasswordError = true;
            }
            else {
                message = "Der Account konnte wegen eines unbekannten Fehlers nicht erstellt werden.";
                isMailError = true;
            }
        }

        return {
            code,
            message,
            isMailError,
            isPasswordError
        }
    };

    const logout = async() => {
        const auth = getAuth();
        await auth.signOut();
        await router.replace("/");
    };

    const hasBoughtCourse = (courseId) => {
        if (!currentUserOverview.current) {
            return false;
        }

        if (currentUserOverview.current.abo) {
            return true;
        }

        for (const id of currentUserOverview.current.boughtCoursesId) {
            if (id == courseId) {
                return true;
            }
        }

        return false;
    }

    const facebookLogin = async () => {
        return await FirebaseWrapper.facebookLogin();
    };

    const googleLogin = async () => {
        return await FirebaseWrapper.googleLogin();
    };

    const appleLogin = async () => {
        return await FirebaseWrapper.appleLogin();
    }

    const resetPassword = async (mail) => {
        await PasswordRecoveryService.recover({ mail } );
    }

    const prioAction = async () => {
        // IsGiftcard -> return true
        if(isGiftcard){
            setCurrentModalErrorMessage(null);
            return true;
        }
        else if(currentUserOverview.current.abo && !(currentUserOverview.current?.canRenewAbo)) {
            setCurrentModalErrorMessage("Du hast den 7hauben Pass bereits erworben.");
            return false;
        } else if(currentCourse
            && (currentUserOverview.current.boughtCoursesId.indexOf(Number.parseInt(currentCourse.id)) !== -1) ) {
            setCurrentModalErrorMessage("Du hast diesen Kurs bereits erworben.");
            return false;
        }
        setCurrentModalErrorMessage(null);
        return true;
    };

    const authValue = {
        isLoggedIn,

        currentUserOverview,
        setCurrentUserOverview,

        currentUserMail,

        courseOverview,
        setCourseOverview,

        updateOwnOverview,

        hasBoughtCourse,

        register,
        logout,
        login,
        resetPassword,

        facebookLogin,
        googleLogin,
        appleLogin,

        currentModal,
        setCurrentModal,

        currentModalErrorMessage,
        setCurrentModalErrorMessage,

        handlePassBuy,
        handlePassGiftcardBuy,
        handleCourseBuy,
        handleCourseGiftcardBuy,

        hideModal,
        removeFromCartEvent,
        isGiftcard,
        setIsGiftcard,

        handleLogin,

        navigateToCourse,

        showMobileOverlay,
        setShowMobileOverlay,

        pageMode,
        setPageMode,

        showBuyModal,

        agbCheck,
        setAgbCheck,

        prioAction
    }

    return (
        <AuthContext.Provider value={authValue}>
            {children}
        </AuthContext.Provider>
    );
}
